import React from "react";

type DSOptionKey = string & { readonly __tag: unique symbol };

export interface DSOption {
  key: DSOptionKey;
  label: string;
  notSelectable: boolean;
}

export const dsOption = (key: string, label: string, notSelectable?: boolean): DSOption => ({
  key: key as DSOptionKey,
  label: label,
  notSelectable: notSelectable ?? false,
});

type DSOptionCollectionKey = string & { readonly __tag: unique symbol };

type DSOptionCollectionType = "select" | "radio";

export interface DSOptionCollection<P extends DSOption> {
  key: DSOptionCollectionKey;
  title: string;
  disabled: boolean;
  type: DSOptionCollectionType;
  options?: P[];
  customSelector?: (model: DependentSelectionsModelProvider) => React.ReactNode;
}

export const dsOptionCollection = <P extends DSOption>(
  key: string,
  title: string,
  options?: P[],
  type?: DSOptionCollectionType,
  disabled?: boolean
): DSOptionCollection<P> => ({
  key: key as DSOptionCollectionKey,
  title: title,
  type: type ?? "select",
  disabled: disabled ?? false,
  options: options,
});

export const dsOptionCollectionWithCustomSelector = <P extends DSOption>(
  key: string,
  title: string,
  customSelector: (model: DependentSelectionsModelProvider) => React.ReactNode,
  disabled?: boolean
): DSOptionCollection<P> => ({
  key: key as DSOptionCollectionKey,
  title: title,
  type: "select",
  disabled: disabled ?? false,
  options: undefined,
  customSelector: customSelector,
});

export interface DependentSelectionsModelProvider {
  selections: DSOption[];
  collections: DSOptionCollection<DSOption>[];
  onSelect: (selected: DSOption[]) => void;
  reset: () => void;
}

export const updateSelection = (
  model: DependentSelectionsModelProvider,
  collectionIndex: number,
  selectedKey: string
): void => {
  model.onSelect([
    ...model.selections.slice(0, collectionIndex),
    model.collections[collectionIndex].options!.find((o) => o.key === selectedKey)!,
  ]);
};

export type DependentSelectionsProps<PR = unknown> = PR & DependentSelectionsModelProvider;
